import { useEffect, useCallback, forwardRef, useState, useRef } from 'react';
import type {
  Ref,
  FunctionComponent,
  ReactNode,
  TransitionEvent,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent
} from 'react';
import styled, { css } from 'styled-components';
import { remToPx } from 'polished';

import { useOuterEvent, useConsolidatedRef, usePrevious, useDirection } from '../../hooks';
import type { AsProp, BaseProps, ForwardProps } from '../../types';
import { getChildOfRootNode, reflow } from '../../utils';
import { defaultThemeProp } from '../../theme';
import {
  resizeDrawerKeyboardStep,
  resizeDrawerMaxWidth,
  resizeDrawerMinWidth
} from '../../styles/constants';

import ResizeHandle from './ResizeHandle';

const drawerMinWidth = remToPx(resizeDrawerMinWidth);

export interface DrawerProps extends AsProp, BaseProps {
  /**
   * If true, the Drawer will be visible.
   * @default false
   */
  open: boolean;
  /** Content of the Drawer. */
  children?: ReactNode;
  /**
   * Determines whether a drop shadow should be visible along the edge of the Drawer for contrast purposes.
   * @default false
   */
  shadow?: boolean;
  /**
   * Used to determine which side of the viewport the Drawer should open from.
   * @default 'right'
   */
  placement?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * The position of the Drawer.
   * @default 'absolute'
   */
  position?: 'absolute' | 'fixed';
  /**
   * Determines the speed of the transition. This value comes from `theme.base.animation.speed`.
   * @default '0.25s'
   */
  transitionSpeed?: string;
  /**
   * The width or height that the Drawer will open to, depending on its placement.
   * If `resizeable`, is true this value will be overridden by the width set by the user.
   * @default '100%'
   */
  size?: string;
  /**
   * If true, the Drawer will be resizable. Will not be applied if `placement` is set to `top` or `bottom`.
   * @default false
   * @internal Can only be used with one drawer at a time.
   */
  resizeable?: boolean;
  /** Callback fired after Drawer opens. */
  onAfterOpen?: () => void;
  /** Callback fired after Drawer closes. */
  onAfterClose?: () => void;
  /** Callback fired before Drawer opens. */
  onBeforeOpen?: () => void;
  /** Callback fired before Drawer closes. */
  onBeforeClose?: () => void;
  /** Callback fired when a click action occurs outside of the Drawer. */
  onOuterClick?: () => void;
  /**
   * If true, removes the component from the real DOM when closed. For full virtual DOM unmount(if necessary), perform at a parent level.
   * @default false
   */
  nullWhenClosed?: boolean;
}

type State = 'open' | 'closed' | 'opening' | 'closing';

export const StyledDrawer = styled.div<Partial<DrawerProps & { state: State }>>(props => {
  const { state, shadow, position, placement, transitionSpeed, size, resizeable, theme } = props;
  let dimension = 'horizontal';
  let axis = 'X';
  let anchor = 'top';

  if (placement === 'top' || placement === 'bottom') {
    dimension = 'vertical';
    axis = 'Y';
    anchor = 'left';
  }

  const translate = state === 'open' || state === 'opening' ? 0 : '100%';
  const speed = transitionSpeed || theme.base.animation.speed;

  return css`
    z-index: ${theme.base['z-index'].drawer};
    position: ${position};
    ${placement}: 0;
    ${anchor}: 0;
    height: ${dimension === 'vertical' ? size : '100%'};
    width: ${dimension === 'horizontal' ? size : '100%'};
    transition-property: transform, box-shadow, opacity;
    transition-duration: max(${speed}, 0.0001s);
    transition-timing-function: ${theme.base.animation.timing.ease};
    transform: ${state === 'open'
      ? 'none'
      : `translate${axis}(${placement === 'top' || placement === 'left' ? '-' : ''}${translate})`};

    ${shadow &&
    css`
      box-shadow: ${theme.base.shadow.high};
    `}

    ${resizeable &&
    css`
      width: ${dimension === 'horizontal' ? 'var(--resize-drawer-width)' : '100%'};
    `}

    ${position === 'fixed' &&
    (placement === 'left' || placement === 'right') &&
    css`
      /* stylelint-disable unit-allowed-list, length-zero-no-unit */
      padding-${placement}: env(safe-area-inset-${placement}, 0px);
      /* stylelint-enable unit-allowed-list, length-zero-no-unit */
    `}
  `;
});

StyledDrawer.defaultProps = defaultThemeProp;

export const Drawer: FunctionComponent<DrawerProps & ForwardProps> = forwardRef(function Drawer(
  props: DrawerProps,
  ref: Ref<HTMLElement>
) {
  const {
    open = false,
    shadow = false,
    position = 'absolute',
    children,
    placement = 'right',
    transitionSpeed,
    size = '100%',
    resizeable = false,
    onAfterOpen,
    onAfterClose,
    onBeforeOpen,
    onBeforeClose,
    onOuterClick,
    nullWhenClosed = false,
    ...restProps
  } = props;

  const drawerRef = useConsolidatedRef(ref);
  const { ltr, rtl } = useDirection();

  const [state, setState] = useState<State>(open ? 'open' : 'closed');

  let prevState = usePrevious(state);
  if (!prevState) prevState = state;

  useOuterEvent(
    'mousedown',
    [drawerRef],
    useCallback(() => {
      if (open) onOuterClick?.();
    }, [open, onOuterClick])
  );

  useEffect(() => {
    if (open && (state === 'closed' || state === 'closing')) {
      onBeforeOpen?.();
      reflow(drawerRef.current);
      setState('opening');
    } else if (!open && (state === 'open' || state === 'opening')) {
      onBeforeClose?.();
      setState('closing');
    } else if (open && state === 'open' && prevState !== 'open') {
      onAfterOpen?.();
    } else if (!open && state === 'closed' && prevState !== 'closed') {
      onAfterClose?.();
    }
  }, [open, state, prevState, onBeforeOpen, onBeforeClose, onAfterOpen, onAfterClose]);

  const onTransitionEnd = useCallback(
    (e: TransitionEvent): void => {
      if (e.target !== drawerRef.current || e.propertyName !== 'transform') return;
      setState(open ? 'open' : 'closed');
    },
    [open]
  );

  const dragStartXRef = useRef<number>();

  useEffect(() => {
    if (!resizeable || !drawerRef.current) return;
    const targetNode = getChildOfRootNode(drawerRef);
    if (!targetNode) return;
    if (!targetNode.style.getPropertyValue('--resize-drawer-width')) {
      targetNode.style.setProperty('--resize-drawer-width', size);
    }
  }, [drawerRef.current]);

  const handleResize = useCallback(
    (e: ReactMouseEvent) => {
      const targetNode = getChildOfRootNode(drawerRef);
      const rect = drawerRef.current?.getBoundingClientRect();
      if (!rect || !targetNode) return;

      dragStartXRef.current = e.clientX;

      const ac = new AbortController();
      targetNode.addEventListener(
        'mousemove',
        moveEvent => {
          if (!dragStartXRef.current || !rect.width) return;

          const initialX = dragStartXRef.current;
          const widthDiff =
            placement === 'right' ? initialX - moveEvent.clientX : moveEvent.clientX - initialX;

          const newWidth = Math.max(widthDiff + rect.width, parseInt(drawerMinWidth, 10));

          // Set root-level CSS variable to communicate width to AppShell
          targetNode.style.setProperty(
            '--resize-drawer-width',
            `clamp(${drawerMinWidth}, ${newWidth}px, ${resizeDrawerMaxWidth})`
          );
        },
        { passive: true, signal: ac.signal }
      );

      targetNode.addEventListener(
        'mouseup',
        () => {
          ac.abort();
        },
        { once: true }
      );
    },
    [placement]
  );

  const keyboardResize = useCallback(
    (e: ReactKeyboardEvent, grabbed: boolean) => {
      if (!grabbed) return;
      const targetNode = getChildOfRootNode(drawerRef);
      const resizeStep = document.documentElement.clientWidth * resizeDrawerKeyboardStep;
      const rect = drawerRef.current?.getBoundingClientRect();
      if (!rect || !targetNode) return;
      if (e.code !== 'ArrowLeft' && e.code !== 'ArrowRight') return;
      e.preventDefault();
      e.stopPropagation();

      const resizeDirection = (e.code === 'ArrowLeft' ? 1 : -1) * (placement === 'right' ? 1 : -1);

      const newWidth = Math.max(
        rect.width + resizeDirection * resizeStep,
        parseInt(drawerMinWidth, 10)
      );
      targetNode.style.setProperty(
        '--resize-drawer-width',
        `clamp(${drawerMinWidth}, ${newWidth}px, ${resizeDrawerMaxWidth})`
      );
    },
    [placement]
  );

  const renderResizeHandle =
    resizeable &&
    state === 'open' &&
    ((placement === 'left' && rtl) || (placement === 'right' && ltr));

  return state === 'closed' && !open && nullWhenClosed ? null : (
    <StyledDrawer
      ref={drawerRef}
      position={position}
      shadow={shadow && open}
      transitionSpeed={transitionSpeed}
      placement={placement}
      size={size}
      resizeable={resizeable}
      open={open}
      state={state}
      onTransitionEnd={onTransitionEnd}
      {...restProps}
    >
      {children}
      {renderResizeHandle && <ResizeHandle onMouseDown={handleResize} onKeyDown={keyboardResize} />}
    </StyledDrawer>
  );
});

export default Drawer;
